import React from "react";
import { IVaccination } from "@/types/vaccination";
import { ListItem, ListItemAction } from "@/components/ui/atoms/MListItem";
import { IconEdit, IconCircleX, IconCheckbox } from "@tabler/icons-react";
import { t } from "i18next";
import { formatPrettyDate } from "@/utils/date-utils";

interface VaccinationCardProps {
  vaccination: IVaccination;
  onEdit: (vaccination: IVaccination) => void;
  onMarkAsDone: (vaccination: IVaccination) => void;
  onDelete: (vaccination: IVaccination) => void;
}

export const VaccinationCard: React.FC<VaccinationCardProps> = ({
  vaccination,
  onEdit,
  onMarkAsDone,
  onDelete,
}) => {
  // Generate secondary text based on vaccination status
  const getSecondaryText = () => {
    if (vaccination.taken) {
      return vaccination.takenOn
        ? t("vaccinations.list.takenOn", {
            date: formatPrettyDate(vaccination.takenOn),
          })
        : t("vaccinations.list.takenOnNotSpecified");
    } else if (vaccination.dueDate) {
      return t("vaccinations.list.dueOn", {
        date: formatPrettyDate(vaccination.dueDate),
      });
    }
    return "";
  };

  // Get tertiary text (reminder days)
  const getTertiaryText = () => {
    if (vaccination.taken) {
      return "";
    }
    const days = vaccination.reminderDays;
    if (!vaccination.taken && days !== null) {
      // Using the correct translation key for reminder days
      switch (days) {
        case 0:
          return t("vaccinations.list.sameDay");
        case 1:
          return t("vaccinations.list.reminderDaysSingular");
        default:
          return t("vaccinations.list.reminderDaysPlural", { days });
      }
    }
    return t("vaccinations.list.noReminder");
  };

  // Create actions for the ListItem
  const getVaccinationActions = (): ListItemAction[] => {
    const actions: ListItemAction[] = [];
    // Define actions based on vaccination state
    if (!vaccination.taken) {
      actions.push({
        id: "mark-as-done",
        label: t("vaccinations.list.markAsDone"),
        icon: <IconCheckbox size={20} />,
        onClick: () => onMarkAsDone(vaccination),
      });
    }

    actions.push(
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => onEdit(vaccination),
      },
      {
        id: "delete",
        label: t("common.delete"),
        icon: <IconCircleX size={20} />,
        onClick: () => onDelete(vaccination),
        className: "delete",
      }
    );
    return actions;
  };

  return (
    <div className="mb-4 relative">
      <ListItem
        usage="crud"
        type="3-line"
        state={"default"}
        variant="purple"
        title={vaccination.name}
        secondaryText={getSecondaryText()}
        tertiaryText={getTertiaryText()}
        actions={getVaccinationActions()}
      />
    </div>
  );
};

export default VaccinationCard;
